<template>
  <ResponsiveLayout>
    <template #header_end><HeaderHelp /></template>
    <Spinner v-if="isLoadingConfig" />
    <div
      v-else
      class="kds-main-wrapper"
    >
      <CorporateHeader />
      <div class="kds-content-block">
        <p
          class="kds-typography-display-small"
          v-html="$t('b2b__verification__eligibility__header')"
        />

        <p
          v-if="!configIsAvailable"
          class="kds-typography-paragraph-large"
          v-html="
            $t(
              'b2b__verification__description_no_eligibility_config_available',
              {
                corporate: escapeHtml(corporateTitle),
              },
            )
          "
        />
        <!-- TODO add specific message here if we can direct the user into a channel specific portal -->
        <p
          v-if="showVerificationSection"
          class="kds-typography-paragraph-large"
          v-html="
            $t('b2b__verification__description', {
              corporate: escapeHtml(corporateTitle),
            })
          "
        />
      </div>

      <GenericSupportModal
        v-if="!configIsAvailable"
        :additional-data="{
          error:
            'The user selected a corporate which is a customer but for which ' +
            'we have not yet received an eligibility list or the eligibility ' +
            'list configuration is not functional',
        }"
        :subject="
          $t('b2b__support_modal_subject__no_eligibility_config_available', {
            corporate: corporateTitle,
          })
        "
        :button-text="
          $t('b2b__support_modal_generic_submit_button_check_with_support')
        "
        @submit-success="handleSupportTicketCreated"
      />

      <BaseBanner
        v-if="showAlreadyUsedBanner"
        variant="info"
        :title="$t('b2b__verification__already_used_error_title')"
        scroll-into-view
      >
        <p
          class="kds-typography-paragraph-medium"
          v-html="$t('b2b__verification__already_used_error_description')"
        />
        <template #actions>
          <BaseButton
            size="small"
            @click="goToAppLogin"
          >
            {{ $t('b2b__verification__already_used_error_cta_open_app') }}
          </BaseButton>

          <GenericSupportModal
            :additional-data="corporateEligibilityData"
            :subject="$t('b2b__support_modal_subject__login_support')"
            @submit-success="handleSupportTicketCreated"
          />
        </template>
      </BaseBanner>

      <BaseBanner
        v-if="showNotEligibleBanner"
        variant="warning"
        :title="$t('b2b__request_support_banner_title')"
        data-qa="request-support-base-banner"
        scroll-into-view
      >
        <p class="kds-typography-paragraph-medium">
          {{ $t('b2b__request_support_banner_description') }}
        </p>
        <NotEligibleSupportModal
          :additional-data="corporateEligibilityData"
          :corporate="corporate"
          :landing-page="landingPage"
          @submit-success="handleSupportTicketCreated"
        >
          <ul>
            <li
              v-for="{ title, key } in showFallbackFields
                ? formFields
                : nonFallbackFields"
              :key="key"
              data-dd-privacy="mask"
              class="request-support-modal__additional_data--item"
            >
              {{ `${title}: ${corporateEligibilityData?.[key]}` }}
            </li>
          </ul>
        </NotEligibleSupportModal>
      </BaseBanner>

      <BaseBanner
        v-if="showGenericErrorBanner"
        variant="warning"
        scroll-into-view
      >
        <p
          class="kds-typography-title-medium"
          v-html="
            $t(
              'b2b_corporate_eligibility_data_not_valid_with_support_email_and_helpscout',
              { VUE_APP_SUPPORT_EMAIL: env.VITE_SUPPORT_EMAIL },
            )
          "
        />
      </BaseBanner>

      <form
        v-if="configIsAvailable"
        id="eligibility_check_form"
        class="kds-content-block"
      >
        <template
          v-for="field in nonFallbackFields"
          :key="field.key"
        >
          <TextField
            v-if="field.type === 'string' || field.type === 'email'"
            v-bind="{ ...fieldAttrs[field.key], ...field.attrs }"
          />

          <DateOfBirthField
            v-if="field.type === 'dob'"
            v-bind="{ ...fieldAttrs[field.key], ...field.attrs }"
            :lang="lang"
          />
        </template>

        <template v-if="showFallbackFields">
          <div
            v-html="
              fallbackFields.length === 1
                ? $t('b2b_corporate_eligibility_data_fallback_single', {
                    fallbackFieldName: escapeHtml(fallbackFields[0].title),
                  })
                : $t(
                    'b2b_corporate_eligibility_data_multiple_matches_more_info',
                  )
            "
          />

          <template
            v-for="field in fallbackFields"
            :key="field.key"
          >
            <TextField
              v-if="field.type === 'string' || field.type === 'email'"
              v-bind="{ ...fieldAttrs[field.key], ...field.attrs }"
            />

            <DateOfBirthField
              v-if="field.type === 'dob'"
              v-bind="{ ...fieldAttrs[field.key], ...field.attrs }"
              :lang="lang"
            />
          </template>
        </template>

        <BaseButton
          v-bind="submitAttrs"
          data-qa="submit"
          :text="$t('generic_check_eligibility')"
        />
      </form>

      <div
        v-if="showExternalPortalOption"
        class="external-portal"
      >
        <span class="kds-typography-ui-subtle-large">
          {{ $t('b2b_corporate_eligibility_external_portal_question') }}
        </span>
        <a
          :href="externalPortalUrl"
          class="external-portal--link kds-color-text-interactive-accent kds-typography-title-medium"
          @click="goToExternalPortal"
        >
          {{
            $t('b2b_corporate_eligibility_external_portal_account', {
              portal: externalPortalName,
            })
          }}
        </a>
      </div>

      <BaseBanner
        v-if="showSamlSSOSignupOption"
        variant="info"
        class="saml-sso-banner"
        :title="
          $t('b2b__corporate_eligibility__label__signup_with_personify_health')
        "
        :description="
          $t(
            'b2b__corporate_eligibility__description__signup_with_personify_health',
          )
        "
      >
        <template #actions>
          <BaseButton
            size="small"
            class="saml-sso-banner__actions"
            @click="redirectToSamlSSO"
          >
            <div class="saml-sso-banner__actions--button-content">
              <span class="kds-typography-paragraph-large">
                {{ $t('b2b__button__signup_with_generic') }}
              </span>
              <img
                src="@shared/assets/img/b2b/personify-health.svg"
                alt="personify health"
                class="saml-sso-banner__actions--button-content--image"
              />
            </div>
          </BaseButton>
        </template>
      </BaseBanner>
    </div>
    <template #footer><FooterLinks v-if="!isFromApp()" /></template>
  </ResponsiveLayout>
</template>

<script setup>
import { ref, computed, watch, watchEffect, onMounted, nextTick } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import useUsStore from '@us/app/stores/useUsStore.js'
import { escapeHtml } from '@shared/utils/escapeHtml'
import { useRoute, useRouter } from 'vue-router'
import * as apiRequest from '@shared/api-endpoints'
import { isFirstNameKey, isLastNameKey, setPageTitle } from '@shared/utils'
import useLandingPageStore from '@shared/stores/useLandingPageStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'

import Tracker from '@shared/Tracker'

import useForm from '@shared/composables/useForm.js'

import assembleKaiaAppDeepLink from '@shared/helpers/assembleKaiaAppDeepLink.js'

import ROUTE from '@us/app/router/appModule/names'
import SIGNUP_METHODS from '@us/app/config/signup-methods.js'

import NotEligibleSupportModal from '@us/app/components/NotEligibleSupportModal.vue'
import GenericSupportModal from '@us/app/components/GenericSupportModal.vue'

import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import DateOfBirthField from '@shared/components/form/DateOfBirthField.vue'
import TextField from '@shared/components/form/TextField.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import CorporateHeader from '@shared/components/CorporateHeader.vue'
import Spinner from '@shared/components/Spinner.vue'
import FooterLinks from '@us/app/components/FooterLinks.vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import HeaderHelp from '@us/app/components/HeaderHelp.vue'
import { useI18n } from 'petite-vue-i18n'
import useSharedStore from '@shared/stores/useSharedStore.js'

const userStore = useUserStore()
const corporateStore = useCorporateStore()
const usStore = useUsStore()
const sharedStore = useSharedStore()
const landingPageStore = useLandingPageStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const configFields = ref([])

const isLoadingConfig = ref(false)
const configIsAvailable = computed(() => configFields.value.length > 0)
const showAlreadyUsedBanner = ref(false)
const showNotEligibleBanner = ref(false)
const showGenericErrorBanner = ref(false)
const showFallbackFields = ref(false)

const lang = computed(() => userStore.lang)

watch(lang, async () => {
  await ensureCorporateEligibilityConfig()
})

const landingPage = computed(() => landingPageStore.landingPage)
const corporate = computed(() => corporateStore.corporate)
const landingPageVerificationType = computed(
  () => landingPage.value?.eligibilityVerificationType,
)
const corporateTitle = computed(
  () =>
    landingPageStore.landingPage?.displayTitle ||
    corporate.value?.title ||
    landingPageStore.landingPage?.slug ||
    '',
)

const externalPortalUrl = computed(
  () =>
    landingPage.value?.eligibilityVerificationExternalUrl ||
    corporate.value?.externalPortalUrl ||
    null,
)
const externalPortalName = computed(
  () =>
    landingPage.value?.eligibilityVerificationExternalPortalName ||
    corporate.value?.externalPortalName ||
    '',
)

const showExternalPortalOption = computed(() => {
  const verificationType =
    landingPageVerificationType.value || corporate.value?.preferredSignupMethod

  return (
    externalPortalUrl.value != null &&
    ['uhchub', 'uhcstore', 'webmd-one'].includes(verificationType)
  )
})

const showSamlSSOSignupOption = computed(
  () =>
    landingPageVerificationType.value === SIGNUP_METHODS.PERSONIFY_HEALTH_SSO ||
    corporate.value?.personifyHealthSponsorId > 0,
)

const corporateEligibilityData = computed(
  () => usStore.corporateEligibilityData,
)

const initialQuery = computed(() => sharedStore.initialQuery)

const kaiaSubmissionTrackEvent = (success, errorMessage) => {
  Tracker.trackKaiaEvent(
    {
      event_name: 'c.lfcycl.submit_eligibility_verification',
      action: 'submit',
      object_type: 'eligibility_verification',
      screen_name: 'onb_eligibility_verification',
    },
    {
      search_success: success,
      error_message: errorMessage,
      landing_page_slug: landingPageStore.landingPage?.slug,
      ...sharedStore.utmData,
    },
  )
}

const formFields = computed(() => {
  return (
    configFields.value
      .filter((field) => !field.is_fallback || showFallbackFields.value)
      ?.map((field) => {
        field.attrs = {}
        if (isFirstNameKey(field.key)) {
          field.attrs.autocomplete = 'given-name'
          field.attrs.hint = t('b2b__verification__first_name_hint')
        }
        if (isLastNameKey(field.key)) {
          field.attrs.autocomplete = 'family-name'
          field.attrs.hint = t('b2b__verification__last_name_hint')
        }
        if (field.type === 'email') field.attrs.type = 'email'
        if (field.type === 'email') field.attrs.autocomplete = 'email'

        field.attrs.label = field.title
        field.attrs.dataQa = field.key

        return field
      }) ?? []
  )
})

const nonFallbackFields = computed(() =>
  formFields.value.filter((field) => !field.is_fallback),
)
const fallbackFields = computed(() =>
  formFields.value.filter((field) => field.is_fallback),
)

const showVerificationSection = computed(
  () => configIsAvailable.value && corporate.value?.key !== 'uhcmedsupp',
)

const ensureCorporateEligibilityConfig = async () => {
  isLoadingConfig.value = true
  try {
    const landingPageSlug =
      landingPageStore.landingPage?.slug || corporate.value?.key
    if (!landingPageSlug) {
      configFields.value = []
      isLoadingConfig.value = false
      return
    }

    const response =
      await usStore.getLandingPageEligibilityConfig(landingPageSlug)
    const memberIdTitle =
      response.memberIdTitle || t('b2b__verification__member_id_label')

    configFields.value = [
      {
        key: 'first_name',
        title: t('b2b__verification__first_name_label'),
        type: 'string',
        is_fallback: false,
      },
      {
        key: 'last_name',
        title: t('b2b__verification__last_name_label'),
        type: 'string',
        is_fallback: false,
      },
      {
        key: 'date_of_birth',
        title: t('b2b__verification__dob_label'),
        type: 'dob',
        is_fallback: false,
      },
      {
        key: 'member_id',
        title: memberIdTitle,
        type: 'string',
        is_fallback: true,
      },
    ]
  } catch {
    configFields.value = []
  }

  isLoadingConfig.value = false
}

const goToAppLogin = async () => {
  window.location.redirectToDeeplink(
    await assembleKaiaAppDeepLink({
      path: 'goto/screen',
      query: { target: 'login' },
    }),
  )
}

const goToExternalPortal = async () => {
  if (externalPortalUrl.value) {
    window.location.assign(externalPortalUrl.value)
  } else {
    console.warn('No external portal URL defined for the corporate')
  }
}

const redirectToNext = () => {
  router.push({
    name: ROUTE.SIGN_UP,
    params: {
      // redirect to the corporate identified during the eligibility check
      slug: usStore.corporateEligibilityData.corporate_key,
    },
  })
}

const redirectToSamlSSO = () => {
  router.push({
    name: ROUTE.SAML_SSO_INITIATED,
    params: {
      slug:
        corporate.value?.key ||
        usStore.corporateEligibilityData.corporate_key ||
        landingPageStore.landingPage?.slug,
    },
  })
}

const formConfig = computed(() =>
  Object.fromEntries(
    formFields.value.map((field) => {
      const validations = ['required']
      if (field.type === 'dob') validations.push('dob')
      if (field.type === 'email') validations.push('email')
      return [field.key, { validations }]
    }),
  ),
)

const form = ref({
  ...corporateEligibilityData.value,
})

watchEffect(() => {
  usStore.corporateEligibilityData = {
    ...corporateEligibilityData.value,
    ...Object.fromEntries(
      Object.entries(form.value).map(([key, value]) => [
        key,
        value?.toString()?.trim(),
      ]),
    ),
  }
})

const { fieldAttrs, submitAttrs, submit } = useForm(form, formConfig, {
  onSubmit: async () => {
    showAlreadyUsedBanner.value = false
    showNotEligibleBanner.value = false
    showGenericErrorBanner.value = false

    Tracker.trackButtonSelect('continue', 'onb_eligibility_verification')
    await usStore.checkCorporateEligibility({
      landing_page_slug: landingPageStore.landingPage?.slug,
      ...Object.fromEntries(
        Object.entries(corporateEligibilityData.value).filter(
          ([, value]) => !!value,
        ),
      ),
    })
  },
  onSuccess: async () => {
    kaiaSubmissionTrackEvent(true, null)
    console.info('corporate eligibility check succeeded')

    redirectToNext()
  },
  onError: async (error) => {
    kaiaSubmissionTrackEvent(false, error)
    console.info('corporate eligibility check failed', error)

    let errorKey = null
    if (error?.response?.status === 403) {
      errorKey = error.response.data?.error

      if (errorKey === 'SWORD_MIGRATION') {
        const corporateKey = error.response.data?.corporate_key
        if (corporateKey) {
          try {
            await apiRequest.loadCorporate({ key: corporateKey })
          } catch {
            // fall back to default Sword URL
          }
        }
        router.push({ name: ROUTE.SWORD_REDIRECT })
        return
      } else if (errorKey === 'MULTIPLE_MATCHES_BUT_UNIQUE_MATCH_REQUIRED') {
        showFallbackFields.value = true
      } else if (errorKey === 'ALREADY_USED') {
        showAlreadyUsedBanner.value = true
      } else if (errorKey === 'NOT_ELIGIBLE') {
        showNotEligibleBanner.value = true
      }
    }

    if (errorKey === undefined) {
      showGenericErrorBanner.value = true
      throw error
    }
  },
})

const handleSupportTicketCreated = () => {
  showAlreadyUsedBanner.value = false
  showNotEligibleBanner.value = false
}

onMounted(async () => {
  setPageTitle(t('b2b__signup__create_account__page_title'))

  if (route.query.retry) return
  await ensureCorporateEligibilityConfig()

  configFields.value.forEach((field) => {
    const initValue = route.query[field.key] || initialQuery.value[field.key]

    if (initValue) {
      form.value[field.key] = initValue
    }
  })

  const shouldSubmit =
    configIsAvailable.value &&
    nonFallbackFields.value.every(({ key }) => !!form?.value?.[key])

  if (shouldSubmit) {
    nextTick(() => {
      submit()
    })
  }
})
</script>

<style lang="scss">
.external-portal {
  margin: auto;
  display: flex;
  gap: $kds-space-1x;
  align-items: baseline;

  &--link {
    text-decoration: none;
  }
}

.saml-sso-banner {
  &__actions {
    @include margin(auto, right);

    &--button-content {
      @include flex-container(row, center, sm);

      &--image {
        height: 25px;
      }
    }
  }
}
</style>
